// Decides whether an auth failure definitively means the user has no usable session (so the app
// must sign out for real and clear their state), or whether it might just be a transient failure
// (dropped connection, timeout, throttling) that a retry could recover from. Signing out on a
// transient failure wipes the user's offline data over nothing more than a flaky connection;
// failing to sign out on a definitive one leaves the app in a stale "signed in" state with no
// session behind it. Every auth path funnels through here so there is one place to reason about.

// Cognito service errors always carry a .code (amazon-cognito-identity-js Client.js derives it
// from the response's __type), and network failures carry code 'NetworkError' - so codes are a
// reliable signal. NotAuthorizedException covers an expired or revoked refresh token, a password
// changed elsewhere, a global sign-out from another device, and a disabled user.
const AUTH_INVALIDATION_ERROR_CODES = new Set([
    'NotAuthorizedException',
    'PasswordResetRequiredException',
    'UserNotConfirmedException',
    'UserNotFoundException',
]);

// Amplify rejects with this bare string when there is no cached identity in storage at all
// (CognitoUserPool.getCurrentUser() found no LastAuthUser key). No request is made, so this can
// never be a connectivity problem - the user is simply not signed in on this device any more.
const NO_CACHED_IDENTITY_ERROR = 'No current user';

// amazon-cognito-identity-js throws these plain Errors from CognitoUser.getSession() *before* it
// attempts any network request, when the locally stored session is missing or incomplete. They
// carry no .code, but they are never transient for exactly that reason: nothing was sent, so a
// weak signal cannot be the cause. Matched by message because that is all these errors carry;
// coupled to amazon-cognito-identity-js (via @aws-amplify/auth 3.4.34). If a future version
// reworded them, the fallback is the safe direction - treating the failure as transient.
const LOCAL_SESSION_UNUSABLE_MESSAGES = new Set([
    'Cannot retrieve a new session. Please authenticate.',
    'Local storage is missing an ID Token, Please authenticate',
    'Username is null. Cannot retrieve a new session',
]);

// The three conditions Amplify itself treats as "this user is no longer valid" when reading user
// data (its currentUserPoolUser only rejects for these, and bypasses every other getUserData
// error). Their .code values are already covered above; matched here belt-and-braces, since the
// exact code Cognito returns for a disabled user is not worth depending on.
const USER_NO_LONGER_VALID_MESSAGES = new Set([
    'Access Token has been revoked',
    'User does not exist.',
    'User is disabled.',
]);

export function isGenuineAuthInvalidationError(error: unknown): boolean {
    if (error === NO_CACHED_IDENTITY_ERROR) {
        return true;
    }
    const { code, message } = (error ?? {}) as { code?: string; message?: string };
    if (code !== undefined && AUTH_INVALIDATION_ERROR_CODES.has(code)) {
        return true;
    }
    if (message === undefined) {
        return false;
    }

    return LOCAL_SESSION_UNUSABLE_MESSAGES.has(message) || USER_NO_LONGER_VALID_MESSAGES.has(message);
}
